import type { CSSProperties, PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';

interface CircularProgressProps {
  size: number;
  strokeWidth: number;
  percentage: number;
  text: string;
}

export const CircularProgressWrapper = ({
  children,
  percentage,
  size,
  strokeWidth,
  ...styledProps
}: PropsWithChildren<
  Pick<CSSProperties, 'maxWidth' | 'position'> &
    Omit<CircularProgressProps, 'text'>
>) => {
  const [progress, setProgress] = useState(0);
  const theme = useTheme();

  useEffect(() => {
    setProgress(percentage);
  }, [percentage]);

  const viewBox = `0 0 ${size} ${size}`;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * Math.PI * 2;
  const dash = (progress * circumference) / 100;

  return (
    <StyledSVG width={size} height={size} viewBox={viewBox} {...styledProps}>
      <circle
        fill="none"
        stroke="#ccc"
        cx={size / 2}
        cy={size / 2}
        r={radius}
        strokeWidth={`${strokeWidth}px`}
      />
      <circle
        fill="none"
        stroke={theme.colors.primary}
        cx={size / 2}
        cy={size / 2}
        r={radius}
        strokeWidth={`${strokeWidth}px`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        // @ts-expect-error array for React.SVGAttributes type
        strokeDasharray={[dash, circumference - dash]}
        style={{ transition: 'all 0.5s' }}
      />
      {children}
    </StyledSVG>
  );
};

const StyledSVG = styled.svg<Pick<CSSProperties, 'maxWidth' | 'position'>>`
  ${({ position = 'relative', maxWidth = '100%' }) => `
  position: ${position};
  max-width: ${maxWidth};
  `}
`;
